// @ts-ignore
import environment from '../environment.json';

import ObservableService from '../core/ObservableService';
import qs from 'qs';
import axios from 'axios';
import User from '../core/User';

export enum AuthEvents {
  LOGIN = 'LOGIN',
  LOGOUT = 'LOGOUT',
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',
}

// this class can be extended to support more errors
export enum LoginError {
  NOT_AUTHORIZED,
  TOKEN_EXPIRED,
}

const loginErrorMap: { [key: string]: LoginError } = {
  'not-authorized': LoginError.NOT_AUTHORIZED,
  'token-expired': LoginError.TOKEN_EXPIRED,
};

// variable in the QS that will be used to pass the token from other part of the app
// e.g. OAuth2 flow
const TOKEN_QUERY_STRING_PROPERTY = 't';

// variable in the QS that will be used to pass metadata related to the user that is logged
// or just logged in - e.g. via the OAuth2 flow
const USER_METADATA_PROPERTY = 'u';

// if the user is not logged already, it will be redirected to this path
const PATH_TO_REDIRECT_NOT_LOGGED_USER = environment.pathToRedirectNotLoggedUser || '/';

// if the user is logged already, it will be redirected to this path
const PATH_TO_REDIRECT_LOGGED_USER = '/home';

// if the user is not authorized, it will be redirected to this path
const NOT_AUTHORIZED_URL = '/?error=not-authorized';

// if the user token is expired, it will be redirected to this path
const TOKEN_EXPIRED_URL = '/?error=token-expired';

// if true, we redirect the user to HTTPS if HTTP is used
const FORCE_HTTPS = environment.forceHTTPS == undefined ? true : environment.forceHTTPS;

// paths that will be allowed is the user is not logged
const NOT_LOGGED_PATHS = ['/', '/register', environment.pathToRedirectNotLoggedUser];

const inDevMode = !process.env.NODE_ENV || process.env.NODE_ENV === 'development';

class AuthService extends ObservableService<AuthEvents> {
  constructor(
    private notLoggedPaths: Set<string> = new Set<string>(),
    private excludeAuthenticationChecks: Set<string> = new Set<string>(),
    private pathToRedirectIfUserNotLogged: string = PATH_TO_REDIRECT_NOT_LOGGED_USER,
    private pathToRedirectIfUserLogged: string = PATH_TO_REDIRECT_LOGGED_USER,
    private pathToRedirectIfTokenExpire: string = TOKEN_EXPIRED_URL,
    private excludeAuthenticationRegex: RegExp[] = []
  ) {
    super();
    NOT_LOGGED_PATHS.forEach((path) => !!path && this.notLoggedPaths.add(path));
  }

  public addNotLoggedPaths(path: string[]) {
    path.filter((singlePath) => !!singlePath).forEach(this.notLoggedPaths.add, this.notLoggedPaths);
  }

  public addExcludeAuthenticationChecks(path: string[]) {
    path.filter((singlePath) => !!singlePath).forEach(this.excludeAuthenticationChecks.add, this.excludeAuthenticationChecks);
  }

  public setPathToRedirectNotLoggedUser(path: string) {
    this.pathToRedirectIfUserNotLogged = path;
  }

  public setPathToRedirectLoggedUser(path: string) {
    this.pathToRedirectIfUserLogged = path;
  }

  public addExcludeAuthenticationRegex(paths: RegExp[]) {
    paths.filter((singlePath) => !!singlePath).forEach((singlePath) => this.excludeAuthenticationRegex.push(singlePath));
  }

  public setPathToRedirectIfTokenExpired(path: string) {
    this.pathToRedirectIfTokenExpire = path;
  }

  public getToken(): string | null {
    if (typeof window !== 'undefined') return window.localStorage.getItem('t');
    return null;
  }

  public storeToken(token: string, tokenExpiration?: Date) {
    localStorage.setItem('t', token);

    if (tokenExpiration) {
      localStorage.setItem('t_expiration', tokenExpiration.toString());
    }

    if (!!token) {
      this.notifyObservers(AuthEvents.LOGIN);
    }
  }

  public storeUserMetadata(metadata: string) {
    if (!!metadata && metadata != 'null') {
      // metadata is a base64-encoded JSON
      // warning! using `atob` to decode the base64 string may broke special characters
      // we use this workaround to convert base64 strings to UTF8 without any charset problem
      // from: https://stackoverflow.com/a/30106551
      // Going backwards: from bytestream, to percent-encoding, to original string.
      const decodedMetadata = decodeURIComponent(
        atob(metadata)
          .split('')
          .map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join('')
      );
      localStorage.setItem('r', decodedMetadata);
    }
  }

  public getExternalId(): string | null {
    return localStorage.getItem('e');
  }

  public getUser(): User | undefined {
    const userStored: string | null = localStorage.getItem('r');
    return userStored ? JSON.parse(userStored) : undefined;
  }

  public hasRole(roleName: string): boolean {
    const user: User | undefined = this.getUser();

    if (user) {
      return user.roles.some((role) => role.name === roleName);
    }

    return false;
  }

  public getEffectivePermissionsForUser(): string[] {
    const user: User | undefined = this.getUser();

    if (!user) {
      return [];
    }

    // collect permissions from user list of permisssions uuids if it exists. If not collect from all user Roles
    const permissions: string[] = user.permissionUUIDs
      ? user.permissionUUIDs
      : user.userRoles.flatMap((role) => role.permissions.map((p) => p.uuid));

    // remove dupes and return
    return Array.from(new Set(permissions));
  }

  public hasAnyPermission(permissions: string[]) {
    const userPermissions = new Set(this.getEffectivePermissionsForUser());
    const result = permissions.filter((p) => userPermissions.has(p));

    return result.length > 0;
  }

  public hasPermissionFor(permissionUUID: string) {
    return this.getEffectivePermissionsForUser().some((p) => p == permissionUUID);
  }

  public ifAllowed<T>(permissionUUID: string, ifTrue: T, ifFalse?: T) {
    if (this.hasPermissionFor(permissionUUID)) {
      return ifTrue;
    }

    return ifFalse;
  }

  public hasAnyRole(roleNames: string[], loggedUser?: User): boolean {
    const user: User | undefined = loggedUser || this.getUser();

    if (user) {
      return user.roles.some((role) => roleNames.includes(role.name));
    }

    return false;
  }

  /**
   * Check if the user has any of the given UserRoles.
   * Substitution for 'hasAnyRole' using the new UserRoles implementation.
   */
  public hasAnyUserRole(userRoleNames: string[], loggedUser?: User): boolean {
    const user: User | undefined = loggedUser || this.getUser();

    if (user) {
      return user.userRoles.some((role) => userRoleNames.includes(role.name));
    }

    return false;
  }

  public getTokenExpiration(): Date | null {
    const tokenExpiration: string | null = localStorage.getItem('t_expiration');

    if (tokenExpiration) {
      return new Date(tokenExpiration);
    }

    return null;
  }

  public checkIfExistsTokenAndIsExpired(): boolean {
    const tokenExpiration = this.getTokenExpiration();
    if (tokenExpiration) return tokenExpiration < new Date();
    return false;
  }

  public storeExternalId(externalId: string) {
    localStorage.setItem('e', externalId);
  }

  public storeUser(user: User) {
    localStorage.setItem('r', JSON.stringify(user));
  }

  public logOut() {
    delete axios.defaults.headers.common['Authorization'];
    localStorage.removeItem('t');
    localStorage.removeItem('r');
    localStorage.removeItem('t_expiration');
    this.notifyObservers(AuthEvents.LOGOUT);
  }

  public isLogged = () => {
    const tokenExpiration = this.getTokenExpiration();

    if (tokenExpiration) {
      return !!this.getToken() && tokenExpiration.getTime() > new Date().getTime();
    }

    return !!this.getToken();
  };

  /**
   * This method takes care of processing external tokens, redirecting the user properly depending on their
   * login state, etc. The result of executing this method in most cases results in a redirect or a noop
   */
  public redirectUserIfNeededDependingOnLoginState(): boolean {
    // Auth token is provided, we store the token and navigate to user home screen
    const qsParams = qs.parse(window.location.search.substring(1));
    const newToken = qsParams[TOKEN_QUERY_STRING_PROPERTY] as string;
    const userMetadata = qsParams[USER_METADATA_PROPERTY] as string;

    let accessError = false;

    if (FORCE_HTTPS && window.location.protocol === 'http:' && !inDevMode) {
      window.location.href = window.location.href.replace('http:', 'https:');
      return true;
    }

    if (newToken !== undefined) {
      if (newToken.length === 0 || newToken === 'null') {
        accessError = true;
      } else {
        this.storeUserMetadata(userMetadata);
        this.storeToken(newToken);
      }
    }

    if (accessError) {
      window.location.href = NOT_AUTHORIZED_URL;
      return true;
    } else {
      // If the path is on the list, skip validations of authentication and no match with the Regex
      if (
        !this.excludeAuthenticationChecks.has(window.location.pathname) &&
        !this.excludeAuthenticationRegex.some((regex) => regex.test(window.location.pathname))
      ) {
        // If user token has expired and is in a non-public part of the app
        // then we redirect to login page with error message
        if (!this.notLoggedPaths.has(window.location.pathname) && this.checkIfExistsTokenAndIsExpired()) {
          //We save the path that the User tried to access, to redirect later
          const currentUrl = encodeURIComponent(window.location.pathname + window.location.search);

          this.notifyObservers(AuthEvents.TOKEN_EXPIRED);
          window.location.href = `${this.pathToRedirectIfTokenExpire}&redirect_uri=${currentUrl}`;
          return true;
        }

        // User is not logged and tried to access a non-public part of the app, we navigate to
        // home to let the user log in
        //We save the path that the User tried to access, to redirect later
        const currentUrl = encodeURIComponent(window.location.pathname + window.location.search);
        const currentURL = new URL(window.location.href);
        const searchParams = currentURL.searchParams;
        const redirectUri = searchParams.get('redirect_uri');

        if (!this.notLoggedPaths.has(window.location.pathname) && !this.isLogged()) {
          if (!redirectUri) {
            // Save the path that the User tried to access, to redirect later
            window.location.href = `${this.pathToRedirectIfUserNotLogged}?redirect_uri=${currentUrl}`;
          } else if (window.location.pathname !== this.pathToRedirectIfUserNotLogged) {
            // Prevent infinite redirection loop by not redirecting again if already on the login page
            window.location.href = this.pathToRedirectIfUserNotLogged;
          }
          return true;
        }

        // If user is logged in and it is in the home screen, then we redirect it to the
        // personal data UI
        if (this.notLoggedPaths.has(window.location.pathname) && this.isLogged()) {
          window.location.href = this.pathToRedirectIfUserLogged;
          return true;
        }
      }
    }

    // No redirections occurred
    return false;
  }

  /**
   * Detects if, depending on the URL or other state, a login error just ocurred, so it can be managed properly
   */
  public loginErrorJustHappened(): LoginError | undefined {
    const qsParams = qs.parse(window.location.search.substring(1));
    const error = qsParams.error as string;

    if (error) {
      return loginErrorMap[error];
    }

    return undefined;
  }
}

export default new AuthService();
